import Vue from "vue";
import _ from "lodash";
import { TOKEN_EXPIRED } from "../auth/store/mutations-types.js";
import { ACTION_TYPES, GETTER_TYPES, MUTATION_TYPES } from "./store_keys.js";
import {
    calculateFields,
    calculateFieldsInlist,
} from "../schema/store_utils.js";
import { refresh } from "./CommonActions.js";
import { store_list_options } from "./CommonMutations.js";

// ie RestModule("http://localhost:8000/api/code_example/");

function handleError(err, commit) {
    if (err.response && err.response.status === 401) {
        // We are no longer logged in!

        commit("AuthModule/" + TOKEN_EXPIRED, null, { root: true });
    }
}

export default function (api, schema) {
    function _getStoredState() {
        // NOTE this is in a function so we don't keep multiple copies of all this data in the local variables
        var result = {
            item: null,

            items: [],
            itemsByID: {},

            code_items: [],
            code_itemsByID: {},

            total_items: 0,
            working: false,
            last_list_options: null,
            last_load: null,
        };

        var stored_state = localStorage.getItem(schema.name);
        if (stored_state) {
            stored_state = JSON.parse(stored_state);

            result = { ...result, ...stored_state }; // overwrite the defaults with the stored State
            result.itemsByID = _.keyBy(result.items || [], "id");
            result.code_itemsByID = _.keyBy(result.code_items || [], "id");
        }

        return result;
    }

    function _storeState(state) {
        const value = _.pick(state, [
            "item",
            "items",
            "code_items",
            "total_items",
        ]);

        localStorage.setItem(schema.name, JSON.stringify(value));
    }

    return {
        namespaced: true,
        state: _getStoredState(),
        persist_reducer(state) {
            return _.pick(state, [
                "item",
                "items",
                "itemsByID",
                "total_items",
                "code_items",
                "code_itemsByID",
            ]);
        },
        mutations: {
            [MUTATION_TYPES.STORE_LIST_OPTIONS]: store_list_options,
            [MUTATION_TYPES.SET_LIST](state, items) {
                state.total_items = items.total_items;
                if ("items" in items) {
                    calculateFieldsInlist(schema, items.items);
                    state.items = items.items;

                    let defaultItem = { [null]: "" };
                    let keyByItems = _.keyBy(items.items, "id");
                    state.itemsByID = { ...keyByItems, ...defaultItem };
                }
                if ("code_items" in items) {
                    calculateFieldsInlist(schema, items.code_items);
                    state.code_items = items.code_items;

                    let defaultItem = { [null]: "" };
                    let keyByItems = _.keyBy(items.code_items, "id");
                    state.code_itemsByID = { ...keyByItems, ...defaultItem };
                }
                _storeState(state);
            },

            [MUTATION_TYPES.CLEAR_LIST](state) {
                state.items = [];
                state.itemsByID = {};

                state.code_items = [];
                state.code_itemsByID = {};

                state.total_items = 0;

                _storeState(state);
            },

            [MUTATION_TYPES.SET_ITEM](state, item) {
                calculateFields(schema, item);
                state.item = item;

                _storeState(state);
            },
            [MUTATION_TYPES.START_WORKING](state) {
                state.working = true;
            },
            [MUTATION_TYPES.FINISH_WORKING](state) {
                state.working = false;
            },
            [MUTATION_TYPES.TEMPLATE](state, template) {
                // set a template to use later..
                state.template = template;
            },
            [MUTATION_TYPES.PATCH_ITEM](state, item) {
                if (state.item && state.item.id === item.id) {
                    // Updating this item
                    state.item = item;
                }

                // Update the items also..
                var found = _.findIndex(state.items, { id: item.id });
                if (found > -1) {
                    state.items.splice(found, 1, item); // Replace it
                }

                _storeState(state);
            },
        },
        actions: {
            refresh,
            [ACTION_TYPES.GET_ALL](
                { rootGetters, dispatch, commit },
                list_options = {},
            ) {
                return new Promise((resolve, reject) => {
                    if (rootGetters["AuthModule/isLoggedIn"]) {
                        dispatch("AuthModule/refreshToken", null, {
                            root: true,
                        });

                        var params = _.cloneDeep(list_options); // sot

                        //TODO Support if code table is called
                        params.offset = list_options.offset || 0;
                        params.limit = list_options.limit || 10;

                        var code_list = list_options["_code"] || false;

                        if (params.query) {
                            if (_.isString(params.query)) {
                                params.query = JSON.stringify(params.query);
                            }
                        } else {
                            params.query = null;
                        }

                        commit(MUTATION_TYPES.STORE_LIST_OPTIONS, list_options);
                        commit(MUTATION_TYPES.START_WORKING);

                        Vue.axios
                            .get(api, { params: params })
                            .then((response) => {
                                commit(MUTATION_TYPES.FINISH_WORKING);

                                const data = {
                                    total_items: response.data.count || 0,
                                };
                                if (code_list) {
                                    data.code_items =
                                        response.data.results || [];
                                } else {
                                    data.items = response.data.results || [];
                                }

                                commit(MUTATION_TYPES.SET_LIST, data);

                                resolve(data);
                            })
                            .catch((err) => {
                                commit(MUTATION_TYPES.FINISH_WORKING);

                                handleError(err, commit);

                                reject(err);
                            });
                    }
                });
            },
            [ACTION_TYPES.GET_BY_ID]({ rootGetters, commit, dispatch }, id) {
                return new Promise((resolve, reject) => {
                    if (rootGetters["AuthModule/isLoggedIn"]) {
                        dispatch("AuthModule/refreshToken", null, {
                            root: true,
                        });

                        commit(MUTATION_TYPES.START_WORKING);

                        Vue.axios
                            .get(api + id + "/", {})
                            .then((response) => {
                                commit(MUTATION_TYPES.FINISH_WORKING);

                                commit(MUTATION_TYPES.SET_ITEM, response.data);
                                resolve(response.data);
                            })
                            .catch((err) => {
                                commit(MUTATION_TYPES.FINISH_WORKING);

                                handleError(err, commit);

                                reject(err);
                            });
                    } else {
                        reject("NOT LOGGED IN");
                    }
                });
            },
        },
        getters: {
            [GETTER_TYPES.ITEM](state) {
                return state.item;
            },
            [GETTER_TYPES.WORKING](state) {
                return state.working;
            },

            [GETTER_TYPES.ITEMS](state) {
                return state.items;
            },
            [GETTER_TYPES.ITEMS_BY_ID](state) {
                return state.itemsByID;
            },

            [GETTER_TYPES.CODE_ITEMS](state) {
                return state.code_items;
            },
            [GETTER_TYPES.CODE_ITEMS_BY_ID](state) {
                return state.code_itemsByID;
            },

            [GETTER_TYPES.TOTAL_ITEMS](state) {
                return state.total_items;
            },
        },
    };
}
